import { Form, Input, Space } from 'antd';
import { Button } from '../ui/button';
import { LockOutlined } from '@ant-design/icons';
import { Loader2 } from 'lucide-react';
import { StyledCard } from './styles';

type PasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type SecuritySectionProps = {
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (values: PasswordForm, form: any) => Promise<void>;
};

export const SecuritySection = ({
  loading,
  onSubmit,
}: SecuritySectionProps) => {
  const [form] = Form.useForm();

  const handleValuesChange = () => {
    // Only validate if the form has been submitted before
    if (form.isFieldsTouched(true)) {
      void form.validateFields();
    }
  };

  const handleSubmit = async (values: PasswordForm) => {
    try {
      await form.validateFields();
      await onSubmit(values, form);
    } catch (err) {
      console.error(err);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  return (
    <StyledCard>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        validateTrigger={['onSubmit']}
      >
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[
            { required: true, message: 'Please input your current password!' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Current Password"
          />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Please input your new password!' },
            { min: 8, message: 'Password must be at least 8 characters!' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="New Password"
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your password!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('The two passwords do not match!'),
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm Password"
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="submit" variant="default" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </StyledCard>
  );
};
